import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, Loader2, Star, Plus, Camera } from "lucide-react";

const FALLBACK_TIERS = [
  {
    id: "self_guided", name: "Self-Guided", price: 99, tagline: "Do it yourself, with confidence.",
    description: "A complete step-by-step roadmap and checklist to convert your LLC to an S-Corp on your own.",
    highlighted: false, cta: "Start Learning",
    features: ["Complete LLC → S-Corp conversion guide", "Interactive implementation checklist", "Form 2553 walkthrough", "Filing deadline calendar", "Reasonable compensation guide", "Document templates", "Video tutorials", "Email support"],
    addons: [{ id: "tax_planning_prep", name: "Tax Planning & Annual Tax Preparation", quote_only: true, description: "Year-round tax planning plus annual business tax return — custom pricing on a quick consultation." }],
  },
  {
    id: "assisted", name: "Assisted", price: 1299, tagline: "We guide you through the process.",
    description: "Everything in Self-Guided, plus a tax pro reviews your documents and answers your questions.",
    highlighted: true, cta: "Get Assistance",
    features: ["Everything in Self-Guided", "1:1 document review", "Form 2553 review", "Reasonable compensation recommendation", "Two strategy calls", "Priority email & chat support"],
    addons: [{ id: "tax_planning_prep", name: "Tax Planning & Annual Tax Preparation", quote_only: true, description: "Year-round tax planning plus annual business tax return — custom pricing on a quick consultation." }],
  },
  {
    id: "full_service", name: "Full-Service", price: 2899, tagline: "We handle everything for you.",
    description: "We file your S-Corp election and set up your payroll and tax accounts end-to-end.",
    highlighted: false, cta: "Go Full-Service",
    features: ["Everything in Assisted", "We prepare and file Form 2553", "Complete S-Corp conversion", "Payroll account setup", "Federal tax account registration", "State tax account registration", "Dedicated implementation specialist", "Done-for-you onboarding", "Post-conversion review"],
    addons: [{ id: "tax_planning_prep", name: "Tax Planning & Annual Tax Preparation (Recurring)", quote_only: true, description: "Ongoing year-round tax planning plus annual business tax return — recurring advisory, custom pricing." }],
  },
];

export default function PricingCards() {
  const [tiers, setTiers] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const load = (attempt = 0) => {
      api.get("/tiers")
        .then(({ data }) => {
          if (active) setTiers(data && data.length ? data : FALLBACK_TIERS);
        })
        .catch(() => {
          if (attempt < 2) setTimeout(() => load(attempt + 1), 1000);
          else if (active) setTiers(FALLBACK_TIERS);
        });
    };
    load();
    return () => { active = false; };
  }, []);

  const toggleAddon = (key) =>
    setSelectedAddons((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleBuy = async (tier) => {
    if (!user) {
      toast("Please sign in to continue", { description: "Create an account or log in to purchase." });
      navigate("/login");
      return;
    }
    const addon_ids = (tier.addons || [])
      .filter((a) => selectedAddons[`${tier.id}:${a.id}`])
      .map((a) => a.id);
    setLoadingId(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier_id: tier.id,
        origin_url: window.location.origin,
        addon_ids,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="pricing-cards">
      <div data-testid="pricing-card-vault"
        className="relative rounded-2xl p-8 flex flex-col bg-[#F4F5F3] border border-dashed border-[#114A31]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
        <span className="absolute -top-3 left-6 bg-[#114A31] text-white text-xs font-bold tracking-wide px-4 py-1 rounded-full flex items-center gap-1">
          <Camera className="h-3 w-3" /> FREE FOREVER
        </span>
        <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C]">Document Vault</h3>
        <p className="mt-1 text-sm text-[#595D5A]">Get organized before you decide.</p>
        <div className="mt-6 flex items-baseline gap-1">
          <span className="font-heading text-4xl font-bold text-[#1C1C1C]">$0</span>
          <span className="text-sm text-[#595D5A]">no card needed</span>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-[#595D5A]">
          Snap photos of receipts and tax documents from your phone. AI reads each one and keeps a running expense summary for tax time.
        </p>
        <ul className="mt-6 space-y-3 flex-1">
          {["Phone camera receipt capture", "AI reads vendor, date, amount & category", "Expense summary by category & year", "CSV export for your bookkeeper", "Secure storage for W-2s, 1099s, prior returns", "Your tax pro can pick it up anytime — no re-uploading"].map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm"><Check className="h-5 w-5 shrink-0 text-[#114A31]" /><span className="text-[#1C1C1C]">{f}</span></li>
          ))}
        </ul>
        <Button onClick={() => navigate(user ? "/vault" : "/register?plan=vault")} data-testid="start-vault-btn"
          className="mt-8 rounded-full py-6 font-medium bg-white text-[#114A31] border border-[#114A31] hover:bg-[#E6F0EB]">
          {user ? "Open my vault" : "Start free"}
        </Button>
      </div>
      {tiers.map((tier) => {
        const owned = user && user.purchased_tiers?.includes(tier.id);
        const highlighted = tier.highlighted;
        const addonTotal = (tier.addons || [])
          .filter((a) => selectedAddons[`${tier.id}:${a.id}`])
          .reduce((s, a) => s + (a.price || 0), 0);
        const total = tier.price + addonTotal;
        return (
          <div
            key={tier.id}
            data-testid={`pricing-card-${tier.id}`}
            className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
              highlighted
                ? "bg-[#114A31] text-white shadow-xl"
                : "bg-white border border-[#E5E5E5] shadow-sm hover:shadow-md"
            }`}
          >
            {highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E6F0EB] text-[#114A31] text-xs font-bold tracking-wide px-4 py-1 rounded-full flex items-center gap-1">
                <Star className="h-3 w-3 fill-[#114A31]" /> MOST POPULAR
              </span>
            )}
            <h3 className={`font-heading text-2xl font-semibold ${highlighted ? "text-white" : "text-[#1C1C1C]"}`}>
              {tier.name}
            </h3>
            <p className={`mt-1 text-sm ${highlighted ? "text-white/70" : "text-[#595D5A]"}`}>{tier.tagline}</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className={`font-heading text-4xl font-bold ${highlighted ? "text-white" : "text-[#1C1C1C]"}`}>
                ${total.toLocaleString()}
              </span>
              <span className={`text-sm ${highlighted ? "text-white/60" : "text-[#595D5A]"}`}>one-time</span>
            </div>
            <p className={`mt-3 text-sm leading-relaxed ${highlighted ? "text-white/80" : "text-[#595D5A]"}`}>
              {tier.description}
            </p>

            <ul className="mt-6 space-y-3 flex-1">
              {tier.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Check
                    className={`h-5 w-5 shrink-0 ${highlighted ? "text-[#7FE3AE]" : "text-[#114A31]"}`}
                  />
                  <span className={highlighted ? "text-white/90" : "text-[#1C1C1C]"}>{f}</span>
                </li>
              ))}
            </ul>

            {(tier.addons || []).length > 0 && (
              <div
                className={`mt-6 space-y-3 rounded-xl p-4 ${
                  highlighted ? "bg-white/10" : "bg-[#F9F9F8] border border-[#E5E5E5]"
                }`}
                data-testid={`addons-${tier.id}`}
              >
                <p className={`text-xs font-bold tracking-wide uppercase ${highlighted ? "text-white/70" : "text-[#595D5A]"}`}>
                  Optional add-ons
                </p>
                {tier.addons.map((a) => {
                  const key = `${tier.id}:${a.id}`;
                  return (
                    <label
                      key={a.id}
                      className="flex items-start gap-3 cursor-pointer"
                      data-testid={`addon-${tier.id}-${a.id}`}
                    >
                      <Checkbox
                        checked={!!selectedAddons[key]}
                        onCheckedChange={() => toggleAddon(key)}
                        data-testid={`addon-checkbox-${tier.id}-${a.id}`}
                        className={`mt-0.5 h-4 w-4 rounded ${
                          highlighted
                            ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-[#114A31]"
                            : "border-[#114A31] data-[state=checked]:bg-[#114A31]"
                        }`}
                      />
                      <span className="flex-1">
                        <span className={`flex items-center justify-between text-sm font-medium ${highlighted ? "text-white" : "text-[#1C1C1C]"}`}>
                          <span className="flex items-center gap-1">
                            <Plus className="h-3 w-3" /> {a.name}
                          </span>
                          <span className={`text-xs font-semibold ${highlighted ? "text-white/80" : "text-[#114A31]"}`}>
                            {a.quote_only ? "Let's talk" : `+$${(a.price || 0).toLocaleString()}`}
                          </span>
                        </span>
                        <span className={`text-xs ${highlighted ? "text-white/60" : "text-[#595D5A]"}`}>
                          {a.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <Button
              onClick={() => handleBuy(tier)}
              disabled={loadingId === tier.id || owned}
              data-testid={`buy-${tier.id}-btn`}
              className={`mt-8 rounded-full py-6 font-medium transition-all ${
                highlighted
                  ? "bg-white text-[#114A31] hover:bg-[#E6F0EB]"
                  : "bg-[#114A31] text-white hover:bg-[#1A6B46]"
              }`}
            >
              {loadingId === tier.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : owned ? (
                "Purchased"
              ) : (
                tier.cta
              )}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
